import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Users } from "lucide-react";
import { useState } from "react";
import { Button } from "./ui/Button";
import { Label } from "./ui/Label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/Select";
import { useToast } from "../hooks/useToast.js";
import { bulkAssignLeads } from "../services/leadService.js";

export default function BulkAssignModal({
  isOpen,
  onClose,
  selectedLeadIds,
  reps = [],
  currentUser,
  onAssigned,
}) {
  const { toast } = useToast();
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);

  const getAssignOptions = () => {
    if (!currentUser) return [];
    if (currentUser.user_role === "business_admin") {
      return [
        { id: currentUser.id, name: "Self" },
        ...reps.map((rep) => ({ id: rep.id, name: rep.name })),
      ];
    }
    return [];
  };

  const handleAssign = async () => {
    if (!assignTo) {
      toast({ variant: "destructive", title: "Select a sales person" });
      return;
    }

    setAssigning(true);
    try {
      const res = await bulkAssignLeads(selectedLeadIds, assignTo);
      toast({ title: res.message || `${selectedLeadIds.length} leads assigned` });
      setAssignTo("");
      onAssigned?.();
      onClose();
    } catch (err) {
      toast({ variant: "destructive", title: "Failed", description: err.message });
    } finally {
      setAssigning(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-xs"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            key="bulk-assign-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 180, damping: 22, mass: 0.5 }}
            className="relative w-full bg-white rounded-t-3xl shadow-lg p-5 z-50"
          >
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1 w-16 rounded-full cursor-pointer"
              onClick={onClose}
            />

            <h2 className="text-base font-semibold text-gray-800 mb-4 text-center">
              Assign {selectedLeadIds.length} Lead{selectedLeadIds.length > 1 ? "s" : ""}
            </h2>

            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <Users size={16} className="text-blue-500" />
                <p className="text-xs text-blue-700">
                  {selectedLeadIds.length} lead{selectedLeadIds.length > 1 ? "s" : ""} selected for assignment
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">Assign To</Label>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger className="h-8 text-xs mt-1">
                    <SelectValue placeholder="Select Sales Person" />
                  </SelectTrigger>
                  <SelectContent>
                    {getAssignOptions().map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <Button
                variant="ghost"
                onClick={onClose}
                className="h-8 px-3 text-xs rounded-full border border-gray-200 text-gray-600"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssign}
                disabled={assigning || !assignTo}
                className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white"
              >
                {assigning ? (
                  <Loader2 size={14} className="animate-spin mr-1" />
                ) : null}
                {assigning ? "Assigning..." : "Assign"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
